import type { InsertDraftStepBetweenRequest } from '@bliks-io/contracts';
import { resolveCurrentLaneKey } from '../../graph/resolveLane';
import { transformDraftNode } from '@/api/draftGraph';
import { addSourceToNodeInputs } from '../../../../../../api/mutations/helpers/draftGraphOperations';
import {
  appendPlaceholderPosition,
  buildPlaceholderStep,
  envelopePlaceholderReconcilers
} from '../../../../../../api/mutations/helpers/placeholderStep';
import { mintTempDraftStepId } from '../../../../../../api/mutations/useProcessDraft/shared';
import { insertDraftStepBetweenApi } from '../../../../../../api/persist/draft';
import { optimisticStartEventInput, startEventIdsOf, stripStartSource } from '../shared';
import type { EdgeActionDeps } from '../types';

/**
 * Insert a new step M between the start event S and the first node T:
 * S → M → T. T can be a step or a draft assignment (suggestion target) —
 * both live in the shared stableStepId space. The transactional endpoint
 * attaches S to M (event input + `previousStep` first-step marking), strips
 * every start-linkage form from T and wires M → T in one call.
 */
export async function insertBetweenStartAndStep(
  startEventId: string,
  targetStepId: string,
  position: { x: number; y: number },
  deps: EdgeActionDeps
): Promise<void> {
  const { processId } = deps;
  const targetEntry = deps.graph.findNode(targetStepId);
  if (!targetEntry || targetEntry.kind === 'event') return;
  const snapshot = targetEntry.node;
  const startEventIds = startEventIdsOf(deps);
  // Strip every start-linkage form from the staged target so once M→T is
  // wired the target ends up with exactly one input row.
  const strippedTargetInputs = stripStartSource(snapshot.inputs ?? [], {
    dropEventInput: true,
    startEventIds
  });
  const laneHint =
    targetEntry.kind === 'step'
      ? resolveCurrentLaneKey(targetEntry.node, deps.getCachedSteps(), deps.getAssigneeLookup?.())
      : undefined;

  const performInsert = async (): Promise<string> => {
    const tempId = mintTempDraftStepId();
    const reconcilers = envelopePlaceholderReconcilers(tempId);
    const body: InsertDraftStepBetweenRequest = {
      source: { kind: 'start-event', eventId: startEventId },
      targetStableStepIds: [targetStepId],
      position,
      ...(laneHint ? { laneKey: laneHint } : {})
    };

    const envelope = await deps.runTransaction({
      optimistic: (prev) => {
        const placeholder = buildPlaceholderStep({
          tempId,
          processId,
          draftId: prev.draft._id,
          inputs: [optimisticStartEventInput(deps, startEventId)],
          laneKey: laneHint
        });
        const rewired = transformDraftNode(prev, targetStepId, (entry) => {
          switch (entry.kind) {
            case 'step':
              return addSourceToNodeInputs(
                { ...entry.node, inputs: strippedTargetInputs, previousStep: null },
                tempId
              );
            case 'assignment':
              return addSourceToNodeInputs({ ...entry.node, inputs: strippedTargetInputs }, tempId);
            case 'event':
              return entry.node;
            default:
              return entry satisfies never;
          }
        });
        return {
          ...rewired,
          draft: { ...rewired.draft, updatedAt: new Date() },
          steps: [...rewired.steps, placeholder]
        };
      },
      optimisticLayout: (prev) => appendPlaceholderPosition(prev, tempId, position),
      // ONE transactional call: attach S→M (marks M first-step), detach S
      // from T (every linkage form) and wire M→T together.
      persist: () => insertDraftStepBetweenApi(processId, body),
      reconcile: reconcilers.reconcile,
      reconcileLayout: reconcilers.reconcileLayout
    });

    return String(envelope.createdNodeIds[0]);
  };

  await performInsert();
}
